// eslint-disable-next-line import/no-extraneous-dependencies
import InfiniteScroll from 'react-infinite-scroll-component';
import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import PostCard from '@/components/Post/PostCard';
import ProjectGroupCard from '@/components/ProjectGroup/ProjectGroupCard';
import { useGetFeedQuery } from '../feed/feed.api.slice';
import useSearchParamActions from '@/customHooks/useSearchParamActions';
import { IFeedItem } from '../feed/feed.d';
import { selectCurrentUser } from '../auth/auth.slice';
import { getDisplayName } from '@/lib/utils';

function FeaturedPostsList({
  onLoadingChange,
  onCountChange,
}: {
  onLoadingChange?: (loading: boolean) => void;
  onCountChange?: (count: number) => void;
}) {
  const [feedData, setFeedData] = useState<IFeedItem[]>([]);
  const [page, setPage] = useState(1);
  const { getSearchParam } = useSearchParamActions();
  const { user } = useSelector(selectCurrentUser);

  const category = getSearchParam('category');
  const subCategory = getSearchParam('subCategory');
  const postedAt = getSearchParam('postedAt');
  const author = getSearchParam('author');
  const toolType = getSearchParam('toolType');
  const controllerType = getSearchParam('controllerType');
  const partner = getSearchParam('partner');
  const ratings = getSearchParam('ratings');
  const sortBy = getSearchParam('sortBy');
  const sortOrder = getSearchParam('sortOrder');
  const contentType = getSearchParam('contentType');
  const softwareUsed = getSearchParam('softwareUsed');
  const searchQuery = getSearchParam('searchQuery');
  const searchMode = getSearchParam('searchMode') as
    | 'title'
    | 'user'
    | undefined;

  // Stable key representing the current filter combination
  const filterKey = `${category}|${subCategory}|${postedAt}|${author}|${toolType}|${controllerType}|${partner}|${ratings}|${sortBy}|${sortOrder}|${contentType}|${softwareUsed}|${searchQuery}|${searchMode}`;

  // Track which filterKey the current feedData was fetched for
  const [loadedFilterKey, setLoadedFilterKey] = useState(filterKey);

  const [hasMore, setHasMore] = useState(true);

  const {
    data: feedResponse,
    isLoading,
    isFetching,
  } = useGetFeedQuery({
    category,
    subCategory,
    postedAt,
    author,
    page,
    toolType,
    controllerType,
    partner,
    ratings,
    sortBy,
    sortOrder,
    contentType,
    softwareUsed,
    searchQuery,
    searchMode,
  });

  // Computed synchronously in render — true as soon as filter params change
  const isFilterLoading = filterKey !== loadedFilterKey;

  useEffect(() => {
    setPage(1);
    setHasMore(true);
  }, [
    category,
    subCategory,
    postedAt,
    author,
    toolType,
    controllerType,
    partner,
    ratings,
    sortBy,
    sortOrder,
    contentType,
    softwareUsed,
    searchQuery,
    searchMode,
  ]);

  useEffect(() => {
    if (!isLoading && !isFetching && feedResponse !== undefined) {
      const items = feedResponse.items ?? [];
      if (page === 1) {
        setFeedData(items);
        setLoadedFilterKey(filterKey);
        onCountChange?.(feedResponse.totalCount ?? items.length);
      } else {
        setFeedData((prev) => {
          const newItems = items.filter(
            (item) => !prev.some((p) => p._id === item._id)
          );
          return [...prev, ...newItems];
        });
      }
      setHasMore(feedResponse.hasMore);
    }
  }, [feedResponse, page, isLoading, isFetching, filterKey, onCountChange]);

  useEffect(() => {
    onLoadingChange?.(isFilterLoading);
    if (isFilterLoading) onCountChange?.(0);
  }, [isFilterLoading, onLoadingChange, onCountChange]);

  const fetchMoreData = () => {
    setPage((prev) => prev + 1);
  };

  /**
   * Render a feed item - either a PostCard or ProjectGroupCard
   */
  const renderFeedItem = (item: IFeedItem) => {
    if (item.type === 'projectGroup') {
      return (
        <ProjectGroupCard
          key={`group-${item._id}`}
          id={item._id}
          name={item.name}
          description={item.description}
          thumbnail={item.thumbnail}
          likesCount={item.likesCount}
          commentsCount={item.commentsCount}
          createdAt={item.createdAt}
          hasLikedByUser={item.hasLikedByUser}
          hasBookmarkedByUser={item.hasBookmarkedByUser}
          isOwner={item.user._id === user?._id}
          authorUsername={item.user.username}
          authorName={getDisplayName(item.user)}
          authorAvatar={item.user.profilePicture}
          isOnefinityOfficial={item.user.isOnefinityOfficial}
          isTrustedMember={item.user.isTrustedMember}
          partnerData={item.partner}
          category={item.category}
          difficulty={item.difficulty}
          averageTotalRating={item.averageTotalRating}
          totalMakes={item.totalMakes}
          materialLinks={item.materialLinks}
          materialUsed={item.materialUsed}
          youtubeUrl={item.youtubeUrl}
        />
      );
    }

    // Default: render as post
    return (
      <PostCard
        key={`post-${item._id}`}
        id={item._id}
        authorName={getDisplayName(item.user)}
        authorAvatar={item.user.profilePicture}
        authorUsername={item.user.username}
        title={item.title}
        description={item.description}
        likesCount={item.likes}
        commentsCount={item.comments}
        postThumbnail={item.postThumbnail ?? null}
        downloadCount={item.downloadCount ?? 0}
        createdAt={item.createdAt}
        category={item.category}
        hasLikedByUser={item.hasLikedByUser}
        isOnefinityOfficial={item.user.isOnefinityOfficial}
        isTrustedMember={item.user.isTrustedMember}
        isOwner={item.user._id === user?._id}
        partnerData={item.partner}
        hasBookmarkedByUser={item.hasBookmarkedByUser}
        averageTotalRating={item.averageTotalRating}
        totalMakes={item.totalMakes}
        isDimensionalMaterial={item.isDimensionalMaterial}
        difficulty={item.difficulty}
        materialUsed={item.materialUsed}
        youtubeUrl={item.youtubeUrl}
        materialLink={item.materialLink}
        materialLinks={item.materialLinks}
      />
    );
  };

  if (isFilterLoading || isLoading) {
    return (
      <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-5 py-2 w-full">
        {Array.from({ length: 12 }).map((_, index) => (
          <Skeleton
            key={`filter-skeleton-${index}`}
            className="w-full h-[420px]"
          />
        ))}
      </div>
    );
  }

  return (
    <InfiniteScroll
      dataLength={feedData.length}
      next={fetchMoreData}
      hasMore={hasMore}
      loader={
        <>
          {Array.from({ length: 12 }).map((_, index) => (
            <Skeleton key={`skeleton-${index}`} className="w-full h-[420px]" />
          ))}
        </>
      }
      className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-5 py-2 w-full"
      style={{ overflow: 'visible' }}
    >
      {feedData
        ?.filter((item) => item !== null && item !== undefined)
        .map((item) => renderFeedItem(item))}
    </InfiniteScroll>
  );
}

export default FeaturedPostsList;
